/**
 * Shopify Storefront API Service
 *
 * Handles GraphQL queries to the Shopify Storefront API using the official client
 * Used to fetch metafields and other data not available in the .json endpoint
 *
 * Configuration is provided via environment variables:
 * - VITE_SHOPIFY_STORE_DOMAIN: Store domain (e.g., "store.myshopify.com")
 * - VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN: Storefront API access token
 */

import { createStorefrontApiClient } from '@shopify/storefront-api-client';
import { ShopifyMetafield } from '../types/shopify';
import { respnse as variantMetafieldCache } from '../data/product-variant-metafield-cache';
import { reportError } from '../config/newrelic';

const STOREFRONT_API_VERSION = '2026-04';

interface MetafieldNode {
  id: string;
  namespace: string;
  key: string;
  value?: string;
  type: string;
  description: string | null;
  reference?: {
    image?: {
      url: string;
      altText: string | null;
      width: number;
      height: number;
    };
    id?: string;
    type?: string;
    handle?: string;
    fields?: Array<{
      key: string;
      value: string;
      type?: string;
      reference?: {
        image?: {
          url: string;
          altText: string | null;
          width: number;
          height: number;
        };
      };
    }>;
  };
}

interface StorefrontResponse {
  product: {
    id: string;
    variants: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          availableForSale: boolean;
          selectedOptions: Array<{
            name: string;
            value: string;
          }>;
          material: MetafieldNode | null;
        };
      }>;
    };
  };
}

export interface VariantData {
  metafieldsHash: Record<number, ShopifyMetafield[]>;
  availabilityHash: Record<number, boolean>;
}

/**
 * Get Storefront API configuration from environment variables
 * Store domain can also be auto-detected from window.Shopify.shop in production
 */
function getStorefrontConfig() {
  let storeDomain: string | null = null;
  let accessToken: string | null = null;

  // Get store domain from window.Shopify.shop (available in Shopify stores)
  if (typeof window !== 'undefined' && (window as any).Shopify?.shop) {
    storeDomain = (window as any).Shopify.shop;
  }

  // Fallback to environment variable
  if (!storeDomain) {
    storeDomain = import.meta.env.VITE_SHOPIFY_STORE_DOMAIN || null;
  }

  // Get access token from environment variable
  accessToken = import.meta.env.VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN || null;

  if (!storeDomain) {
    console.warn('Unable to detect Shopify store domain. Metafields will not be available.');
    console.warn('Set VITE_SHOPIFY_STORE_DOMAIN in your environment variables.');
    return null;
  }

  if (!accessToken) {
    console.warn('Shopify Storefront API access token not configured. Metafields will not be available.');
    console.warn('Set VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN in your environment variables.');
    return null;
  }

  return {
    storeDomain,
    accessToken,
  };
}

/**
 * Create and return a configured Storefront API client
 */
function getStorefrontClient() {
  const config = getStorefrontConfig();

  if (!config) {
    return null;
  }

  return createStorefrontApiClient({
    storeDomain: config.storeDomain,
    apiVersion: STOREFRONT_API_VERSION,
    publicAccessToken: config.accessToken,
  });
}

/**
 * Fetch metafields and inventory availability for product variants by product handle
 *
 * @param productHandle - The product handle (URL-friendly slug)
 * @returns Metafields and `availableForSale` hashes, keyed by variant ID
 */
export async function fetchVariantData(productHandle: string): Promise<VariantData> {
  const client = getStorefrontClient();

  if (!client) {
    return { metafieldsHash: {}, availabilityHash: {} };
  }

  const query = `
    query getProductVariantMetafields($handle: String!) {
      product(handle: $handle) {
        id
        handle
        variants(first: 100) {
          edges {
            node {
              id
              title
              availableForSale
              selectedOptions {
                name
                value
              }
              material: metafield(namespace: "custom", key: "material") {
                id
                namespace
                key
                value
                type
                description
                reference {
                  ... on Metaobject {
                    fields {
                      key
                      value
                      type
                      reference {
                        ... on MediaImage {
                          image {
                            url
                            altText
                            width
                            height
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  try {
    const { data, errors } = await client.request<StorefrontResponse>(query, {
      variables: { handle: productHandle },
    });

    if (errors) {
      const err = new Error(`GraphQL errors fetching variant metafields: ${JSON.stringify(errors)}`);
      console.error(err.message);
      reportError(err, { context: 'fetchVariantData' });
      return processVariantData((variantMetafieldCache as any).data);
    }

    return processVariantData(data);
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    console.warn('Failed to fetch variant metafields, falling back to cache:', err);
    reportError(err, { context: 'fetchVariantData' });

    return processVariantData((variantMetafieldCache as any).data);
  }
}

function processVariantData(data: StorefrontResponse | null | undefined): VariantData {
  const metafieldsHash: Record<number, ShopifyMetafield[]> = {};
  const availabilityHash: Record<number, boolean> = {};

  if (!data?.product?.variants?.edges) {
    return { metafieldsHash, availabilityHash };
  }

  data.product.variants.edges.forEach(({ node }) => {
    const variantId = parseInt(node.id.split('/').pop() || '', 10);
    if (node.availableForSale !== undefined) {
      availabilityHash[variantId] = node.availableForSale;
    }
  });

  // First pass: collect metafields by foam package option
  const metafieldsByFoamPackage: Record<string, ShopifyMetafield[]> = {};

  data.product.variants.edges.forEach(({ node }) => {
    // Find the foam package option
    const foamPackageOption = node.selectedOptions.find(
      opt => opt.name.toLowerCase() === 'foam package' || opt.name.toLowerCase() === 'foampackage'
    );

    if (!foamPackageOption) {
      return;
    }

    const foamPackageName = foamPackageOption.value;

    // Process material metafield if it exists and hasn't been processed for this foam package
    if (node.material && !metafieldsByFoamPackage[foamPackageName]) {
      const metafields: ShopifyMetafield[] = [];

      // Handle metaobject reference - extract image, title, and description
      if (node.material.reference?.fields) {
        node.material.reference.fields.forEach((field: any) => {
          // Extract image URL from image field
          if (field.key === 'image' && field.reference?.image) {
            metafields.push({
              id: `${node.material.id}_image`,
              namespace: node.material.namespace,
              key: 'material_image',
              value: field.reference.image.url,
              type: 'file_reference',
              description: node.material.description,
            });
          }
          // Extract title
          else if (field.key === 'title' && field.value) {
            metafields.push({
              id: `${node.material.id}_title`,
              namespace: node.material.namespace,
              key: 'material_title',
              value: field.value,
              type: 'single_line_text_field',
              description: node.material.description,
            });
          }
          // Extract description
          else if (field.key === 'description' && field.value) {
            metafields.push({
              id: `${node.material.id}_description`,
              namespace: node.material.namespace,
              key: 'material_description',
              value: field.value,
              type: 'multi_line_text_field',
              description: node.material.description,
            });
          }
        });
      }

      if (metafields.length > 0) {
        metafieldsByFoamPackage[foamPackageName] = metafields;
      }
    }
  });

  // Second pass: apply metafields to all variants with the same foam package
  data.product.variants.edges.forEach(({ node }) => {
    const variantId = parseInt(node.id.split('/').pop() || '', 10);

    const foamPackageOption = node.selectedOptions.find(
      opt => opt.name.toLowerCase() === 'foam package' || opt.name.toLowerCase() === 'foampackage'
    );

    if (foamPackageOption) {
      const foamPackageName = foamPackageOption.value;
      const metafields = metafieldsByFoamPackage[foamPackageName];

      if (metafields && metafields.length > 0) {
        metafieldsHash[variantId] = metafields;
      }
    }
  });

  return { metafieldsHash, availabilityHash };
}

/**
 * Helper function to get a specific metafield value from a variant's metafields
 *
 * @param metafields - Array of metafields
 * @param namespace - Metafield namespace
 * @param key - Metafield key
 * @returns Metafield value or null if not found
 */
export function getMetafieldValue(
  metafields: ShopifyMetafield[] | undefined,
  namespace: string,
  key: string
): string | null {
  if (!metafields) {
    return null;
  }

  const metafield = metafields.find(
    (mf) => mf.namespace === namespace && mf.key === key
  );

  return metafield?.value || null;
}
